import * as CookieConsent from 'vanilla-cookieconsent'

import { ConsentCategory, ConsentState } from '../types'
import { getConsentTranslations } from './consentTranslations'

// Fully self-hosted — no site ID, no CDN, no external config. `run()` is
// fire-and-forget: subscribers registered via onConsentChange below (from
// gtag.ts/facebookPixel.ts/hubspot.ts, called synchronously right after this)
// pick up the real decision once the library's async init resolves, via the
// onConsent/onChange callbacks — same "catches up shortly after" pattern the
// previous CookieHub polling implementation relied on, just event-driven now.
export const initCookieConsent = (): void => {
  if (typeof window === 'undefined') {
    return
  }
  try {
    CookieConsent.run({
      mode: 'opt-in',
      categories: {
        necessary: { readOnly: true, enabled: true },
        analytics: {},
        marketing: {},
      },
      language: {
        default: 'fi',
        translations: getConsentTranslations(),
      },
      onConsent: notifySubscribers,
      onChange: notifySubscribers,
    })
  } catch {
    // A broken consent banner must never break page rendering (FR-010).
  }
}

export const hasConsent = (category: ConsentCategory): boolean => {
  if (typeof window === 'undefined') {
    return false
  }
  try {
    return CookieConsent.acceptedCategory(category)
  } catch {
    return false
  }
}

export const getConsentState = (): ConsentState => ({
  analytics: hasConsent('analytics'),
  marketing: hasConsent('marketing'),
  necessary: true,
})

const consentStatesEqual = (a: ConsentState, b: ConsentState): boolean =>
  a.analytics === b.analytics &&
  a.marketing === b.marketing &&
  a.necessary === b.necessary

type Notify = () => void
const subscribers = new Set<Notify>()

// Called from CookieConsent.run()'s onConsent/onChange config callbacks
// (real events, not a poll) whenever the visitor's decision is set or changed.
const notifySubscribers = (): void => {
  subscribers.forEach((notify) => notify())
}

export const onConsentChange = (
  callback: (state: ConsentState) => void
): (() => void) => {
  if (typeof window === 'undefined') {
    return () => {}
  }

  let lastState: ConsentState | null = null
  const notify = (): void => {
    const state = getConsentState()
    if (!lastState || !consentStatesEqual(lastState, state)) {
      lastState = state
      callback(state)
    }
  }

  notify()
  subscribers.add(notify)

  return () => {
    subscribers.delete(notify)
  }
}
